import CryptoJS from 'crypto-js'

/**
 * Normalizes an array of headers for consistent hashing.
 *
 * Rules:
 * - Lowercase every header
 * - Trim whitespace
 * - Remove empty headers
 * - Keep unicode/special characters
 * - Sort alphabetically
 *
 * @param {string[]} headers - Array of header strings
 * @returns {string[]} Normalized and sorted array of headers
 */
export const normalizeHeaders = headers => {
  if (!Array.isArray(headers)) {
    return []
  }

  return headers
    .map(header => {
      if (typeof header !== 'string') {
        return String(header || '')
          .trim()
          .toLowerCase()
      }
      return header.trim().toLowerCase()
    })
    .filter(header => header.length > 0) // Remove empty headers
    .sort() // Sort alphabetically
}

/**
 * Generates a SHA256 hash of normalized headers.
 *
 * This creates a consistent format identifier that can be used to
 * match previous upload formats for auto-filling mappings.
 *
 * @param {string[]} headers - Array of header strings
 * @returns {string} SHA256 hash in hex format
 */
export const generateFormatHash = headers => {
  const normalized = normalizeHeaders(headers)

  if (normalized.length === 0) {
    throw new Error('Cannot generate hash from empty headers array')
  }

  // Create a deterministic string representation
  const headerString = JSON.stringify(normalized)

  // Generate SHA256 hash using crypto-js (browser-compatible)
  const hash = CryptoJS.SHA256(headerString)
  return hash.toString(CryptoJS.enc.Hex)
}
